import * as def from '@/definitions'
import { TextInfo } from '@/utilityModels/textInfo'
import { Layer } from './layer';
import { TextLayer } from './textLayer';
import { ArrayHelper } from '@/helper/arrayHelper';
import { StringHelper } from '@/helper/stringHelper';
import { Rectangle } from '@/utilityModels/rectangle';
import { Vector2 } from '@/utilityModels/vector2';
import { DebugHelper } from '@/helper/debugHelper';

// 拡張テキストレイヤー
// 文字ごとの文法情報を持つ
export class ExTextLayer extends TextLayer
{
    public get layerType() : string { return "exText"; }

    // 文法情報配列
    // 改行なしの全文字それぞれに対応する(ただし改行文字は抜く)
    private _textInfoArray : Array<TextInfo> = new Array();
    public get textInfoArray() { return this._textInfoArray; }
    public set textInfoArray(textInfoArray) { this._textInfoArray = textInfoArray; }
    public addTextInfo(textInfo) { this._textInfoArray = ArrayHelper.add(this._textInfoArray, textInfo); }
    public insertTextInfo(textInfo, index) { this._textInfoArray = ArrayHelper.insert(this._textInfoArray, textInfo, index); }
    public removeTextInfo(index) { this._textInfoArray = ArrayHelper.removeAt(this._textInfoArray, index); }
    public replaceTextInfo(textInfo, index) 
    { 
        this._textInfoArray = ArrayHelper.removeAt(this._textInfoArray, index);
        this._textInfoArray = ArrayHelper.insert(this._textInfoArray, textInfo, index);
    }

    // 指定がない場合の文法情報 文字列がないときはこの値を使用する
    private _baseTextInfo : TextInfo = new TextInfo();
    public get baseTextInfo() { return this._baseTextInfo; }
    public set baseTextInfo(baseTextInfo) { this._baseTextInfo = baseTextInfo; }

    // 行数取得
    public getLineCount(isPlaceholder = false)
    {
        var text = isPlaceholder ? ExTextLayer.PLACEHOLDER : this.text;
        // 改行文字で分割
        var lines = text.split(/\r?\n/g);
        return lines.length;
    }

    // 行の文字数取得
    public getCharacterCount(lineIndex, isPlaceholder = false)
    {
        var text = isPlaceholder ? ExTextLayer.PLACEHOLDER : this.text;
        // 改行文字で分割
        var lines = text.split(/\r?\n/g);
        return lines[lineIndex].length;
    }

    // 改行を除いた全文字数取得
    public getTextCount(isPlaceholder = false) 
    { 
        var text = isPlaceholder ? ExTextLayer.PLACEHOLDER : this.text;
        // 改行文字を空白に
        var totalText = text.replace(/\r?\n/g, "");
        // 文字数を返す
        return totalText.length;
    }

    // 行番号 / 文字番号を元にして以下を取得
    // character : [文字]
    // index : トータル文字番号
    // textInfo : [TextInfo]
    public getCharacterInfo(lineIndex, characterIndex, isPlaceholder = false)
    {
        var text = isPlaceholder ? ExTextLayer.PLACEHOLDER : this.text;
        // 改行文字で分割
        var lines = text.split(/\r?\n/g);
        var searchIndex = 0;
        for(var l = 0; l < lines.length; ++l)
        {
            var line = lines[l];
            if(line.length == 0)
            {
                if(l == lineIndex && 0 == characterIndex)
                {
                    // 見つかった
                    var textInfo = this.baseTextInfo;
                    if(this.textInfoArray.length > 0)
                    {
                        textInfo = searchIndex < this.textInfoArray.length ? this.textInfoArray[searchIndex] : this.textInfoArray[this.textInfoArray.length - 1];
                    }
                    return {
                        character : "",
                        index : searchIndex > 0 ? searchIndex - 1 : 0,
                        textInfo : textInfo
                    }
                }
            }
            else
            {
                for(var c = 0; c < line.length; ++c)
                {
                    if(l == lineIndex && c == characterIndex)
                    {
                        // 見つかった
                        var character = line.substr(characterIndex, 1);
                        var textInfo = this.textInfoArray.length > searchIndex ? this.textInfoArray[searchIndex] : this.baseTextInfo;

                        if(textInfo.isEmQuad)
                        {
                            // 全角が設定されている場合は、文字列を全角変換する
                            character = StringHelper.toEmQuadString(character);
                        }
                        character = this.fixText(character);
                        return { 
                            character : character, 
                            index : searchIndex,
                            textInfo : textInfo
                        };
                    }
                    searchIndex++;
                }
            }
        }

        // 見つからなかった
        return null;
    }

    // 一部文字化けするテキストの修正
    private fixText(text : string)
    {
        var fixed = text.replace(/～/g, "〜");
        return fixed;
    }

    // 文法情報全体を書き換える
    public setWholeTextInfo(textInfo : TextInfo)
    {
        // 全ての文法情報を削除する
        this.textInfoArray = new Array();
        // 基本文法情報の変更
        this.baseTextInfo = textInfo.clone();
        if(this.getTextCount() > 0)
        {
            // テキストが存在している場合
            for(var i = 0; i < this.getTextCount(); ++i)
            {
                this.addTextInfo(textInfo.clone());
            }
        }
    }

    // 文法情報全体を基本文法情報でリセットする
    public resetWhileTextInfo()
    {
        // 全ての文法情報を削除する
        this.textInfoArray = new Array();
        if(this.getTextCount() > 0)
        {
            // テキストが存在している場合
            for(var i = 0; i < this.getTextCount(); ++i)
            {
                this.addTextInfo(this.baseTextInfo.clone());
            }
        }
    }

    // テキストを書き換える
    // 削除されたテキスト: 文法情報も削除
    // 追加されたテキスト: 
    //  追加位置の直前に文字がある場合 -> 直前文字の文法情報を適用
    //  追加位置の直前に文字はないが、直後に文字がある場合(1文字以上で先頭位置) -> 直後の文字の文法情報を適用
    //  文字列がないところに新規入力 -> baseTextInfoを適用
    public changeText(text : string)
    {
        // 現在のテキストと新しいテキストを文字列比較する
        if(this.text == text)
        {
            // 同じなら何もしない
            return;
        }

        // 改行込みの文字列を保持
        var oldText = this.text;
        var newText = text;

        // 改行を抜いて以前のテキストと新しいテキストを保持
        var oldString = this.text.replace(/\r?\n/g, "");
        var newString = text.replace(/\r?\n/g, "");
        
        if(oldString == newString)
        {
            // テキストの変化がないときは、改行のみなど
            // テキストをそのまま設定して終わる
            this.text = text;
            return;
        }

        if(oldString.length == 0)
        {
            // テキストの新規入力の場合は文法を文字数だけ追加
            this.text = text;
            this.textInfoArray = new Array();
            for(var i = 0; i < newString.length; ++i)
            {
                this.addTextInfo(this.baseTextInfo.clone());
            }
            return;
        }

        if(newString.length == 0)
        {
            // テキストが消去された場合は文法を消去
            this.text = text;
            this.textInfoArray = new Array();
            return;
        }

        // 変更前のテキストのカレント位置までの文字数
        var oldLength = oldString.length;
        var pos = this.lastEndPosition;
        if(pos == 0)
        {
            // 最初の位置にあるときは文字数 == 0
            oldLength = 0;
        }
        else
        {
            var indexPair = this.getLineAndCharacterIndexFromPosition(pos);
            var info = this.getCharacterInfo(indexPair.lineIndex, indexPair.characterIndex);
            if(info != null) { oldLength = indexPair.isFirstInLine ? info.index : info.index + 1; }
        }
        if(oldLength < 0) oldLength = 0;

        // 新しいテキストを設定する
        this.text = text;

        // 変更後のテキストのカレント位置までの文字数
        var newLength = newString.length;
        var pos = this.endPosition;
        if(pos == 0)
        {
            // 最初の位置にあるときは文字数 == 0
            newLength = 0;
        }
        else
        {
            var indexPair = this.getLineAndCharacterIndexFromPosition(pos);
            var info = this.getCharacterInfo(indexPair.lineIndex, indexPair.characterIndex);
            if(info != null) { newLength = indexPair.isFirstInLine ? info.index : info.index + 1; }
        }
        if(newLength < 0) newLength = 0;

        // それぞれ決められた文字数までの文字列を比較する
        var jsdiff = require('diff');
        var oldStringPart = oldString.substr(0, oldLength);
        var newStringPart = newString.substr(0, newLength);
        var diff = jsdiff.diffChars(oldStringPart, newStringPart);

        // 削除文字が存在しているか？
        var removeCount = 0;
        for(var i = 0; i < diff.length; ++i)
        {
            if(diff[i].removed) removeCount++;
        }
        if(removeCount == 0 && oldString.length > newString.length)
        {
            // 文字が減っているのに削除文字が存在しない場合は、次の文字をDeleteした場合
            // 判定文字数を1文字増やして再比較する
            oldLength++;
            oldStringPart = oldString.substr(0, oldLength);
            diff = jsdiff.diffChars(oldStringPart, newStringPart);
        }
        
        // 削除した文字を逆順で探す
        var oldIndex = oldStringPart.length;
        for(var i = diff.length - 1; i >= 0; --i)
        {
            var part = diff[i];
            var value = part.value;
            oldIndex -= value.length;
            if(part.removed)
            {
                // 削除 oldIndex文字目 から value の文字数
                for(var d = oldIndex + value.length - 1; d >= oldIndex; --d)
                {
                    this.removeTextInfo(d);
                }
            }
            else if(part.added)
            {
                // 追加
                oldIndex += value.length;
            }
            else
            {
                // 同じ
            }
        }
        // 追加した文字列を昇順で探す
        var newIndex = 0;
        for(var i = 0; i < diff.length; ++i)
        {
            var part = diff[i];
            var value = part.value;
            if(part.removed)
            {
                // 削除
            }
            else if(part.added)
            {
                // 追加 newIndex文字目 から value の文字数
                var applyIndex = newIndex - 1;
                if(applyIndex < 0) { applyIndex = 0; }
                var applyTextInfo = (applyIndex < this.textInfoArray.length) ? this.textInfoArray[applyIndex] : this.baseTextInfo;
                for(var a = newIndex; a < newIndex + value.length; ++a)
                {
                    if(a < this.textInfoArray.length)
                    {
                        this.insertTextInfo(applyTextInfo.clone(), a);
                    }
                    else
                    {
                        this.addTextInfo(applyTextInfo.clone());
                    }
                }
                newIndex += value.length;
            }
            else
            {
                // 同じ
                newIndex += value.length;
            }
        }
    }

    // テキストのバックアップのためのオブジェクトを取得する
    public getBackupTextObject() : any
    {
        // 文法情報を返す
        return this.textInfoArray;
    }

    // テキストのバックアップのためのオブジェクトを適用する
    public setBackupTextObject(object : any)
    {
        // 文法情報を適用する
        if(object instanceof Array)
        {
            this.textInfoArray = new Array();
            for(var info of object)
            {
                if(info instanceof TextInfo)
                {
                    this.textInfoArray.push(info.clone());
                }
            }
        }
    }

    // 最終テキスト領域
    // 当たり判定をとるときに使用する
    private lastTextPositionArea : Rectangle = new Rectangle();
    public updateTextPostionArea(x : number, y : number, width : number, height : number)
    {
        // レスポンジブさせないようにオブジェクトを直接書き換える
        this.lastTextPositionArea.x = x;
        this.lastTextPositionArea.y = y;
        this.lastTextPositionArea.width = width;
        this.lastTextPositionArea.height = height;
    }
    // ある点が領域内に存在するか
    public isPointContainedInText(x : number, y : number)
    {
        // 点を逆回転
        var inversed = (new Vector2(x, y)).rotateDegrees(-this.rotate, this.x, this.y);
        var area = this.lastTextPositionArea;
        return (area.width > 0 && area.height > 0 && area.left <= inversed.x && inversed.x <= area.right && area.top <= inversed.y && inversed.y <= area.bottom);
    }

    public isPointContained(x : number, y : number)
    {
        return super.isPointContained(x, y) || this.isPointContainedInText(x, y);
    }

    // 複製
    public clone()
    {
        var cloned = new ExTextLayer();
        this.cloneParameter(cloned);
        return cloned;
    }

    public cloneParameter(layer : ExTextLayer)
    {
        for(var i = 0; i < this._textInfoArray.length; ++i)
        {
            layer._textInfoArray.push(this._textInfoArray[i].clone());
        }
        layer._baseTextInfo = this._baseTextInfo.clone();
        
        super.cloneParameter(layer);
    }

    // JSON
    public toJSON()
    {
        var jsonObj = {};
        jsonObj = this.saveParameterToJson(jsonObj);
        return jsonObj;
    }

    public saveParameterToJson(jsonObject)
    {
        jsonObject.textInfoArray = this._textInfoArray;
        jsonObject.baseTextInfo = this._baseTextInfo;

        jsonObject = super.saveParameterToJson(jsonObject);

        return jsonObject;
    }

    public static createFromJsonData(jsonData : any) : ExTextLayer
    {
        var layer = new ExTextLayer();
        layer.loadParameterFromJson(jsonData);
        return layer;
    }

    public loadParameterFromJson(jsonData)
    {
        if(jsonData.textInfoArray != null)
        {
            for(var i = 0; i < jsonData.textInfoArray.length; ++i)
            {
                this._textInfoArray.push(TextInfo.createFromJsonData(jsonData.textInfoArray[i]));
            }
        }
        if(jsonData.baseTextInfo != null) 
        { 
            this._baseTextInfo = TextInfo.createFromJsonData(jsonData.baseTextInfo);
        }

        super.loadParameterFromJson(jsonData);
    }
}